// src/lib/stays/stayHostTrust.ts
// Anti-scam trust layer (2026-11-02), client-safe pure helpers:
//
//   Badge 1 / Badge 2  — the host's Zamzi-earned tier ("New host · 0 completed
//                        stays" / "Verified host · N completed stays · 0
//                        safety incidents"), stamped by the
//                        stayHostTrustTierSweep on the profile. Derive the
//                        display copy here so every surface says the same
//                        words, and FAIL SAFE: an unstamped profile reads
//                        "new" — the stricter tier for the guest.
//   Badge 3            — claimed external trust profile links (Airbnb,
//                        Booking.com, Google Maps, LekkeSlaap,
//                        TravelGround, SafariNow, Expedia, Other). We display
//                        the host's claim; guests tap through and read the
//                        reviews on the OTA's own site. Claimed ≠ verified,
//                        and claims never count toward "Proven".
//   The deposit rule   — enforcement lives in the engine
//                        (functions/src/index.ts resolveStayDepositPercent)
//                        and its checkout mirror (bookingCharge.ts); this
//                        file only carries the human-readable rule.

import type { ProfileData, StayExternalTrustLink } from "@/types/profile";

export const STAY_HOST_TRUST_TIERS = ["new", "proven"] as const;
export type StayHostTrustTierDisplay = (typeof STAY_HOST_TRUST_TIERS)[number];

export const STAY_EXTERNAL_TRUST_SITES = [
  { key: "google", label: "Google Maps profile" },
  { key: "airbnb", label: "Airbnb profile" },
  { key: "booking", label: "Booking.com page" },
  { key: "lekkeslaap", label: "LekkeSlaap listing" },
  { key: "travelground", label: "TravelGround listing" },
  { key: "safarinow", label: "SafariNow listing" },
  { key: "expedia", label: "Expedia page" },
  { key: "other", label: "Other site" },
] as const satisfies ReadonlyArray<{ key: StayExternalTrustLink["site"]; label: string }>;

const SITE_LABELS = new Map(STAY_EXTERNAL_TRUST_SITES.map((entry) => [entry.key, entry.label]));

export const stayExternalTrustSiteLabel = (site: string, label?: string | null): string => {
  const key = String(site || "").trim().toLowerCase();
  if (key === "other" && label?.trim()) return label.trim().slice(0, 60);
  return SITE_LABELS.get(key as StayExternalTrustLink["site"]) ?? "External profile";
};

export const MAX_STAY_EXTERNAL_TRUST_LINKS = 6;

/**
 * Domain pinning (2026-11-03, "how do I know it is not stolen?"): a claim of
 * "Airbnb profile" must actually open on airbnb.com — otherwise a scammer
 * could paste any https URL and borrow a trusted label. A registry-site link
 * whose hostname does not belong to that site is dropped by the sanitizer and
 * the save message tells the host why. "Other" carries no domain promise, so
 * the full URL is shown on the guest pill instead.
 */
export const STAY_EXTERNAL_TRUST_SITE_DOMAINS: Record<StayExternalTrustLink["site"], readonly string[]> = {
  google: ["google.com", "g.page"],
  airbnb: ["airbnb.com", "airbnb.co.za"],
  booking: ["booking.com"],
  lekkeslaap: ["lekkeslaap.co.za"],
  travelground: ["travelground.com", "travelground.co.za"],
  safarinow: ["safarinow.com", "safarinow.co.za"],
  expedia: ["expedia.com", "expedia.co.za"],
  other: [],
};

export const stayExternalTrustUrlMatchesSite = (
  site: string,
  url: string,
): boolean => {
  const key = String(site || "").trim().toLowerCase() as StayExternalTrustLink["site"];
  const domains = STAY_EXTERNAL_TRUST_SITE_DOMAINS[key] ?? [];
  if (domains.length === 0) {
    return true; // "other" — no domain promise made
  }
  try {
    const hostname = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
    return domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
};

export type StayTrustBadgeView = {
  tier: StayHostTrustTierDisplay;
  /** The one-line pill: the guest's takeaway in plain words. */
  label: string;
  /** Longer line shown right under the pill. */
  detail: string;
  completedStays: number;
};

/**
 * The Badge 1 → Badge 2 ladder. She fixed the rules on 2026-11-02: nobody is
 * camouflaged as proven, nobody is punished for being new — the unknown is
 * labelled, the scam economics are printed on the listing itself.
 */
export const deriveStayTrustBadge = (profile: Partial<ProfileData> | null | undefined): StayTrustBadgeView => {
  const tier: StayHostTrustTierDisplay = profile?.stayHostTrustTier === "proven" ? "proven" : "new";
  const completedStays =
    typeof profile?.stayCompletedStays === "number" && Number.isFinite(profile.stayCompletedStays) && profile.stayCompletedStays > 0
      ? Math.floor(profile.stayCompletedStays)
      : 0;

  if (tier === "proven") {
    return {
      tier,
      completedStays,
      label: `Verified host · ${completedStays} completed stay${completedStays === 1 ? "" : "s"} · 0 safety incidents`,
      detail:
        "Proven only by completed stays on Zamzi Stay with zero verified safety reports. Any verified report freezes this listing the same day, straight to the founder.",
    };
  }
  return {
    tier,
    completedStays: 0,
    label: `New host · ${completedStays} completed stay${completedStays === 1 ? "" : "s"} on Zamzi Stay`,
    detail:
      "Protected by the 10% deposit guarantee — a guest's maximum one-off loss if a stay goes wrong is the deposit. A verified report freezes this listing the same day, straight to the founder.",
  };
};

/** One plain sentence with the rule, for settings surfaces. */
export const STAY_DEPOSIT_RULE_COPY =
  "Platform rule: the deposit is 10% for new hosts. Hosts proven by 3+ completed stays and zero safety reports may set up to 30% — never more.";

const SITE_KEYS = new Set(STAY_EXTERNAL_TRUST_SITES.map((entry) => entry.key as string));

/**
 * Server-side sanitizer for claimed links (used by the owner PATCH route):
 * https-only URLs (the guest's browser opens them — nothing else), site must
 * be from the registry or degrades to "other", labels capped, and a claimed
 * link is never repeated. Claims are display data only — they never feed the
 * trust tier, so sanitizing hard is nicety, not safety.
 */
export const sanitizeStayExternalTrustLinks = (input: unknown): StayExternalTrustLink[] => {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const links: StayExternalTrustLink[] = [];
  for (const raw of input.slice(0, MAX_STAY_EXTERNAL_TRUST_LINKS)) {
    if (!raw || typeof raw !== "object") continue;
    const site = typeof (raw as { site?: unknown }).site === "string" ? String((raw as { site?: unknown }).site).trim().toLowerCase() : "";
    const key = SITE_KEYS.has(site) ? (site as StayExternalTrustLink["site"]) : "other";
    const urlCandidate = String((raw as { url?: unknown }).url || "").trim();
    if (!/^https:\/\/[^\s]+$/i.test(urlCandidate)) continue;
    // Domain pinning: a claimed "Airbnb profile" that does not actually open
    // on Airbnb is dropped — a borrowed label is exactly the trick this round
    // exists to stop.
    if (!stayExternalTrustUrlMatchesSite(key, urlCandidate)) continue;
    if (seen.has(urlCandidate)) continue;
    seen.add(urlCandidate);
    const labelRaw = typeof (raw as { label?: unknown }).label === "string" ? String((raw as { label?: unknown }).label).trim().slice(0, 60) : "";
    links.push({
      site: key,
      url: urlCandidate,
      ...(key === "other" && labelRaw ? { label: labelRaw } : {}),
    });
  }
  return links;
};
